/**
 * <SelectionActionBar> — la barre du geste de groupe (molecule).
 *
 * Quand une sélection multiple est ouverte, un bandeau vient sous la liste et porte LE geste :
 * « Archiver (3) » sur les activités à faire, « Restaurer (2) » sur les archivées. Le nombre vit
 * dans le bouton, comme dans le legacy — pas dans une pastille à côté.
 *
 * Elle ne se place pas elle-même, comme `<HelpBubble>` : la page décide où la poser (en bas de sa
 * zone de contenu, au-dessus de la barre d'onglets). Elle ne dessine que le bandeau.
 *
 * Entièrement contrôlée : le nombre choisi, l'envoi en cours et le geste arrivent en props.
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { type ButtonColor } from "../../atoms/Buttons/Button/Button"
import { type IconName } from "../../atoms/icons/Icon"

/** Le bouton ne s'étale pas sur un grand écran : au-delà, une barre d'action cesse d'être un geste. */
const MAX_ACTION_WIDTH = 400

export type SelectionActionBarProps = {
    /** Le nombre d'éléments choisis — il s'écrit entre parenthèses dans le bouton. */
    count: number
    /** Le verbe du geste — « Archiver », « Restaurer ». */
    actionLabel: string
    iconName?: IconName
    /** Couleur du bouton — `primary` par défaut. */
    color?: ButtonColor
    onAction?: () => void
    /** L'envoi est en cours : le bouton tourne et ne répond plus. */
    loading?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function SelectionActionBar(props: SelectionActionBarProps) {
    const { count, actionLabel, iconName, color = "primary", onAction, loading = false, style, testID } = props

    return (
        <View testID={testID} style={[styles.bar, style]}>
            <View style={styles.inner}>
                <Button
                    label={`${actionLabel} (${count})`}
                    iconName={iconName}
                    color={color}
                    onPress={onAction}
                    disabled={count === 0 || loading}
                    loading={loading}
                    fullWidth
                />
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Ni surface ni filet : c'est `<PageTemplate footer>` qui les dessine.
    bar: {
        paddingVertical: theme.spacing["1"],
    },
    inner: {
        width: "100%",
        maxWidth: MAX_ACTION_WIDTH,
        alignSelf: "center",
    },
}))
